import React, { useCallback, useEffect, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Label } from "expano-components";

import MainFilters from "./components/MainFilters";

import {
  getHeadStyle,
  getRowStyle,
  getCellStyle,
  getRowData,
  moveChannelCampaignGroupProduct,
  removeChannelCampaignGroupProduct,
  addChannelCampaignGroupProduct,
  exportToAllegro,
} from "../../helpers";
import Status from "./components/Status";
import ProgressBar from "./components/ProgressBar";
import Placements from "./components/Placements";
import UpdateChannelCampaignGroupModal from "./components/UpdateChannelCampaignGroupModal";

import { getChannelCampaignGroupKind } from "utils/select_options/channel_campaign_group_kind_options";
import UpdateChannelCampaignModal from "./components/UpdateChannelCampaignModal";
import StatsModal from "./components/StatsModal";

import { ReactComponent as EditIcon } from "icons/edit.svg";

function CampaignsList({
  channel_id,
  min_roas,
  max_roas,
  min_average_use_daily_budget,
  max_average_use_daily_budget,
  query_filters,
  load_more_ref,
  handleSetQueryFilters,
  data,
  meta,
  fetchNextPage,
  is_fetching_next_page,
  campaigns_today_cost,
  campaigns_stats,
  setCampaignToSync,
  setCampaignSynced,
  show_archived,
  refetch,
  interval_days,
}) {
  const getCampaignGroupCost = useCallback(
    (id) =>
      campaigns_today_cost.find(
        ({ channel_campaign_group_id }) => channel_campaign_group_id === id,
      )?.total_cost || 0,
    [campaigns_today_cost],
  );

  const getTypeByRowDepth = (row_depth) => {
    switch (row_depth) {
      case 0:
        return {
          records_key: "channel_campaigns",
          record_id: "channel_campaign_id",
        };
      case 1:
        return {
          records_key: "channel_campaign_groups",
          record_id: "channel_campaign_group_id",
        };
      case 2:
        return {
          records_key: "channel_campaign_group_products",
          record_id: "product_id",
        };
      default:
        return {};
    }
  };

  const fetchItemStats = useCallback(
    (
      row_depth,
      id,
      channel_campaign_group_id,
      product_id,
      field,
      postfix = "",
    ) => {
      const { records_key, record_id } = getTypeByRowDepth(row_depth);

      if (!records_key) {
        return null;
      }

      const id_to_find =
        records_key === "channel_campaign_group_products" ? product_id : id;

      const data = (
        (records_key === "channel_campaign_group_products"
          ? campaigns_stats?.[records_key]?.[channel_campaign_group_id] || []
          : campaigns_stats?.[records_key]) || []
      )?.find((item) => item?.[record_id] === id_to_find)?.[field];

      if (data === null || data === undefined) {
        return "-";
      }
      return `${data}${postfix ? ` ${postfix}` : ""}`;
    },
    [campaigns_stats],
  );

  const fetchStatsModalProps = (
    row_depth,
    id,
    channel_campaign_id,
    channel_campaign_group_id,
    product_id,
  ) => {
    if (row_depth === 0) {
      return {
        channel_campaign_id: id,
      };
    }
    if (row_depth === 1) {
      return {
        channel_campaign_id: channel_campaign_id,
        channel_campaign_group_id: id,
      };
    }
    if ([2, 3].includes(row_depth)) {
      return {
        channel_campaign_id: channel_campaign_id,
        channel_campaign_group_id: channel_campaign_group_id,
        product_id,
      };
    }
  };

  const [expanded, setExpanded] = React.useState({});
  const [loaded_children, setLoadedChildren] = React.useState({});
  const [loading_rows, setLoadingRows] = React.useState({});
  const [moving_products, setMovingProducts] = React.useState({});
  const [update_channel_campaign, setUpdateChannelCampaign] =
    React.useState(null);
  const [update_channel_campaign_group, setUpdateChannelCampaignGroup] =
    React.useState(null);
  const [stats_modal_data, setStatsModalData] = React.useState(null);

  const fetchChildren = useCallback(
    async ({
      row_id,
      type,
      channel_campaign_group_id,
      channel_campaign_id,
      refetch = false,
      kind,
    }) => {
      if (!refetch && (loaded_children[row_id] || loading_rows[row_id])) return;

      try {
        setLoadingRows((prev) => ({ ...prev, [row_id]: true }));

        if (type === "channel_campaign") {
          const response = await getRowData({
            type,
            channel_id,
            channel_campaign_id,
            show_archived,
          });
          const children_key = show_archived
            ? "channel_campaign_groups"
            : "active_channel_campaign_groups";
          let children_data = (response?.[children_key] || []).map((item) => ({
            ...item,
            channel_campaign_id,
            row_id,
          }));
          setLoadedChildren((prev) => ({
            ...prev,
            [row_id]: children_data,
          }));
        }

        if (type === "channel_campaign_group") {
          const response = await getRowData({
            type,
            channel_id,
            channel_campaign_id,
            channel_campaign_group_id,
          });
          const channel_campaign_groups_siblings = data
            .find(({ id }) => Number(id) === Number(channel_campaign_id))
            ?.channel_campaign_groups.filter(
              ({ kind, status }) =>
                ["ACTIVE", "PAUSED"].includes(status) &&
                ["best_sellers", "high_budget", "standard"].includes(kind),
            );
          const {
            channel_campaign_group_products_with_offers,
            channel_campaign_group_products_without_offers,
          } = response;

          let children_data_with_offers = (
            channel_campaign_group_products_with_offers || []
          ).map((item) => ({
            ...item,
            channel_campaign_groups_siblings,
            channel_campaign_id,
            row_id,
          }));

          let children_data_without_offers = (
            channel_campaign_group_products_without_offers || []
          ).map((item) => ({
            ...item,
            channel_campaign_groups_siblings,
            channel_campaign_id,
            row_id,
          }));

          children_data_with_offers = [
            ...children_data_with_offers,
            {
              id: channel_campaign_group_id,
              name: "Produkty bez oferty",
              is_custom_row: true,
              type: "without_offers_channel_campaign_group",
              channel_campaign_id,
              channel_campaign_group_id,
            },
            {
              id: channel_campaign_group_id,
              name: "Produkty archiwalne",
              is_custom_row: true,
              type: "archival_offers_channel_campaign_group",
              channel_campaign_id,
              channel_campaign_group_id,
            },
            kind === "neutral"
              ? {
                  id: channel_campaign_group_id,
                  name: "Produkty do dodania",
                  is_custom_row: true,
                  type: "waiting_channel_campaign_group_products",
                  channel_campaign_id,
                  channel_campaign_group_id,
                }
              : undefined,
          ].filter(Boolean);
          setLoadedChildren((prev) => ({
            ...prev,
            [row_id]: children_data_with_offers,
            [`without_offers_${row_id}`]: children_data_without_offers,
          }));
        }
        if (type === "archival_offers_channel_campaign_group") {
          const { channel_campaign_group_offer_stats_sorted } =
            await getRowData({
              type,
              channel_id,
              channel_campaign_id,
              channel_campaign_group_id,
            });
          let children_data = (
            channel_campaign_group_offer_stats_sorted || []
          ).map((item) => ({
            ...item,
            channel_id,
            channel_campaign_id,
            channel_campaign_group_id,
            row_id,
          }));
          setLoadedChildren((prev) => ({
            ...prev,
            [row_id]: children_data,
          }));
        }
        if (type === "waiting_channel_campaign_group_products") {
          const { products_sorted } = await getRowData({
            type,
            channel_id,
            channel_campaign_id,
            channel_campaign_group_id,
          });
          let children_data = (products_sorted || []).map((item) => ({
            ...item,
            channel_id,
            channel_campaign_id,
            channel_campaign_group_id,
            row_id,
          }));
          setLoadedChildren((prev) => ({
            ...prev,
            [row_id]: children_data,
          }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingRows((prev) => ({ ...prev, [row_id]: false }));
      }
    },
    [channel_id, loaded_children, data, show_archived],
  );

  useEffect(() => {
    setExpanded({});
    setLoadedChildren({});
    setLoadingRows({});
    setMovingProducts({});
  }, [show_archived]);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        header: () => <span>Kampania</span>,
        cell: ({ row }) => (
          <div
            style={{
              paddingLeft: `${row.depth * 20}px`,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            {row.getCanExpand() && (
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: !row.getIsExpanded()
                    ? "rotate(-90deg)"
                    : "rotate(0)",
                }}
              >
                <path
                  fill="none"
                  d="M9 1L5 5L1 1"
                  stroke="#192027"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {row.original?.is_custom_row ? (
              <div className="product-data">
                <p>
                  <strong>{row?.original?.name}</strong>
                </p>
              </div>
            ) : (
              <div
                className={`product-data ${row.depth === 2 ? "full-width" : ""}`}
              >
                <p>
                  <strong>
                    <a
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-link"
                      href={
                        row.depth === 0
                          ? `/campaigns/channels/${channel_id}/channel_campaigns/${row?.original?.id}`
                          : row.depth === 1
                            ? `/campaigns/channels/${channel_id}/channel_campaigns/${row?.original?.channel_campaign_id}/channel_campaign_groups/${row?.original?.id}`
                            : `/products/${row?.original?.product?.id}`
                      }
                    >
                      {row?.original?.name || row?.original?.product?.name}
                    </a>
                  </strong>
                  <p>
                    <span>
                      {loading_rows[row.id] ? (
                        <p style={{ marginLeft: 8 }}>Ładowanie...</p>
                      ) : (
                        row?.original?.external_id ||
                        row?.original?.product?.sku
                      )}

                      {[2, 3].includes(row.depth) && (
                        <>
                          {" | "}
                          {row?.original?.product?.brand?.name
                            ? row.original.product.brand.name
                            : "-"}
                        </>
                      )}
                      {row.depth === 0 &&
                        row.original.is_created_in_allegro &&
                        " | Utworzona przez Allegro"}
                    </span>
                    {[2, 3].includes(row.depth) && (
                      <span className="link-label-group">
                        <a
                          className="link-label"
                          href={`/products_manage/product_groups/${row.original.product?.product_group_id}/specification/products/${row.original.product?.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Label type="fill" color="#36bcff" text="PIM" />
                        </a>
                        <a
                          className="link-label"
                          href={`https://wms.tradesk.pl/products/find_by_code?code=${row.original?.product?.sku}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Label type="fill" color="#ae22ff" text="WMS" />
                        </a>
                      </span>
                    )}
                  </p>
                  {row.depth === 0 && row?.original?.sync_needed && (
                    <p className="text-danger">
                      Kampania grupowa wymaga synchronizacji z Allegro!
                    </p>
                  )}
                  {!loading_rows[row.id] &&
                    row.depth === 1 &&
                    row?.original?.sync_needed && (
                      <p className="text-danger">
                        Wymaga synchronizacji z Allegro!{" "}
                        <button
                          type="button"
                          className="btn btn-sm btn-blue btn-icon"
                          onClick={async (e) => {
                            try {
                              e.stopPropagation();
                              setLoadingRows((prev) => ({
                                ...prev,
                                [row.id]: true,
                              }));
                              await exportToAllegro({
                                channel_id,
                                channel_campaign_id:
                                  row?.original.channel_campaign_id,
                                channel_campaign_group_id: row?.original.id,
                              });
                              setLoadedChildren((prev) => {
                                const currentCampaignKey = `channel_campaign_${row?.original.channel_campaign_id}`;
                                if (!prev[currentCampaignKey]) return prev;

                                if (
                                  prev[currentCampaignKey]
                                    .filter(({ id }) => id !== row.original.id)
                                    .every(({ sync_needed }) => !sync_needed)
                                ) {
                                  setCampaignSynced(
                                    row?.original.channel_campaign_id,
                                  );
                                }
                                return {
                                  ...prev,
                                  [currentCampaignKey]: prev[
                                    currentCampaignKey
                                  ].map((item) => {
                                    const sync_needed = [
                                      row.original.id,
                                    ].includes(item.id)
                                      ? false
                                      : item.sync_needed;
                                    return { ...item, sync_needed };
                                  }),
                                };
                              });
                            } finally {
                              setLoadingRows((prev) => ({
                                ...prev,
                                [row.id]: false,
                              }));
                            }
                          }}
                        >
                          <span>
                            🔄<em className="m-l-5">Sync.</em>
                          </span>
                        </button>
                      </p>
                    )}
                  {row.depth === 3 &&
                    row.original.type ===
                      "channel_campaign_group_offer_stat" && (
                      <p className="text-danger">Produkt archiwalny</p>
                    )}
                  {[2, 3].includes(row.depth) && (
                    <div className="keywords-group">
                      <a
                        href={`/products_manage/product_groups/${row.original.product.product_group_id}/product_cards/channel_product_groups/${row.original.channel_product?.channel_product_group_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="keywords-group__edit"
                      >
                        <EditIcon />
                      </a>
                      <div className="keywords-group__grid">
                        {row.original.channel_product?.keyword_list?.map(
                          (keyword) => (
                            <a
                              className="keyword-link"
                              href={`https://allegro.pl/listing?string=${keyword}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {keyword}
                            </a>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </p>
              </div>
            )}
          </div>
        ),
      },
      {
        id: "kind",
        header: () => <span>Rodzaj</span>,
        cell: ({ row }) => {
          if (row.original?.is_custom_row) {
            return null;
          }
          if (row.depth === 1) {
            return (
              <span className="text-nowrap">
                {getChannelCampaignGroupKind(row?.original?.kind)}
              </span>
            );
          }
          if (row.depth === 2) {
            if (moving_products[row.id]) return <p>Proszę czekać...</p>;
            if (
              ["neutral"].includes(row?.original?.channel_campaign_group?.kind)
            ) {
              return (
                <div className="buttons-group">
                  <button
                    type="button"
                    className="btn btn-sm btn-blue"
                    style={{
                      backgroundColor: "rgb(244, 79, 130)",
                    }}
                    onClick={() =>
                      removeProduct({
                        row_id: row.id,
                        channel_campaign_id: row.original.channel_campaign_id,
                        channel_campaign_group_id:
                          row.original.channel_campaign_group?.id,
                        channel_campaign_group_product_id: row.original.id,
                      })
                    }
                  >
                    <span className="text-nowrap">Usuń</span>
                  </button>
                </div>
              );
            }
            return (
              <div className="buttons-group">
                {row.original.channel_campaign_groups_siblings
                  ?.filter(
                    ({ kind }) =>
                      kind !== row.original.channel_campaign_group?.kind,
                  )
                  .map(({ id: new_channel_campaign_group_id, kind }) => (
                    <button
                      key={new_channel_campaign_group_id}
                      type="button"
                      className="btn btn-sm btn-blue"
                      style={{
                        backgroundColor:
                          kind === "standard"
                            ? "rgb(244, 176, 79)"
                            : kind === "best_sellers"
                              ? "rgb(136, 215, 77)"
                              : "rgb(244, 79, 130)",
                      }}
                      onClick={() =>
                        moveProduct({
                          row_id: row.id,
                          channel_campaign_id: row.original.channel_campaign_id,
                          channel_campaign_group_id:
                            row.original.channel_campaign_group?.id,
                          channel_campaign_group_product_id: row.original.id,
                          new_channel_campaign_group_id,
                        })
                      }
                    >
                      <span className="text-nowrap">
                        {getChannelCampaignGroupKind(kind)}
                      </span>
                    </button>
                  ))}
              </div>
            );
          }
          if (row.depth === 3) {
            if (moving_products[row.id]) return <p>Proszę czekać...</p>;
            if (
              ["neutral"].includes(
                row?.original?.channel_campaign_group?.kind,
              ) &&
              row?.original?.type === "product"
            ) {
              return (
                <div className="buttons-group">
                  <button
                    type="button"
                    className="btn btn-sm btn-blue"
                    onClick={() =>
                      addProduct({
                        row_id: row.id,
                        channel_campaign_id: row.original.channel_campaign_id,
                        channel_campaign_group_id:
                          row.original.channel_campaign_group?.id,
                        product_id: row.original?.product?.id,
                      })
                    }
                  >
                    <span className="text-nowrap">Dodaj</span>
                  </button>
                </div>
              );
            }
          }
        },
      },
      {
        id: "category",
        header: () => <span>Kategoria</span>,
        cell: ({ row }) =>
          row.depth === 0 &&
          !row.original?.is_custom_row && (
            <>
              {row?.original?.root_taxon?.id
                ? `${row.original.root_taxon.name} [${row.original.root_taxon.identifier}]`
                : ""}
            </>
          ),
      },
      {
        id: "status",
        header: () => <span>Status</span>,
        cell: ({ row }) =>
          !row.original?.is_custom_row && (
            <Status
              status={row?.original?.status}
              onClick={() => {
                if (row.depth === 0) {
                  setUpdateChannelCampaign({
                    id: row.original.id,
                    channel_id,
                    name: row.original.name,
                    status: row.original.status,
                  });
                }
                if (row.depth === 1) {
                  setUpdateChannelCampaignGroup({
                    id: row.original.id,
                    channel_id,
                    name: row.original.name,
                    status: row.original.status,
                    channel_campaign_id: row.original.channel_campaign_id,
                    sales_period_standard_cpc:
                      row.original.sales_period_standard_cpc,
                    budget_daily_amount: row.original.budget_daily_amount,
                    sales_period_min_cpc_percent:
                      row.original.sales_period_min_cpc_percent,
                    sales_period_max_cpc_percent:
                      row.original.sales_period_max_cpc_percent,
                    sales_period_min_cpc: row.original.sales_period_min_cpc,
                    sales_period_max_cpc: row.original.sales_period_max_cpc,
                    bidding_max_cpc_amount: row.original.bidding_max_cpc_amount,
                  });
                }
              }}
            />
          ),
      },
      {
        id: "placements",
        header: () => <span>Miejsca emisji</span>,
        cell: ({ row }) =>
          !row.original?.is_custom_row && (
            <Placements data={row?.original?.placements} />
          ),
      },
      {
        id: "counts",
        header: () => (
          <>
            <span>Ilość produktów</span>
            <p className="extra">Ilość ofert</p>
          </>
        ),
        cell: ({ row }) =>
          !row.original?.is_custom_row && (
            <>
              {row.depth < 2 && (
                <>
                  <p>{row.original?.products_count || "-"}</p>
                  <p>{row.original?.offers_count || "-"}</p>
                </>
              )}
              {[2, 3].includes(row.depth) &&
                row.original.offers_data?.length > 0 && (
                  <>
                    {row.original.offers_data.map(({ external_id, url }) => (
                      <p key={external_id}>
                        <a
                          href={url}
                          className="text-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {external_id}
                        </a>
                      </p>
                    ))}
                  </>
                )}
            </>
          ),
      },
      {
        id: "budget",
        header: () => (
          <>
            <span>Budżet dzienny</span>
            <p className="extra">Budżet wykorzystany</p>
          </>
        ),
        cell: ({ row }) => {
          if (row.original?.is_custom_row) {
            return null;
          }
          if (row.depth === 0) {
            return (
              <>
                <p>
                  Dzienny:{" "}
                  {row.original?.campaign_group_total_budget_daily_amount}
                </p>
                <p>
                  Całkowity:{" "}
                  {row.original?.campaign_group_total_budget_total_amount}
                </p>
              </>
            );
          }
          if (row.depth !== 1) {
            return "-";
          }
          const total_budget = row.original?.budget_daily_amount || 0;
          const used_budget = getCampaignGroupCost(row.original.id);
          const available_budget =
            (row.original?.budget_daily_amount || 0) - used_budget;

          const total_cost = fetchItemStats(
            row.depth,
            row.original.id,
            row.original?.channel_campaign_group?.id,
            row.original?.product?.id,
            "total_cost",
          );

          const average_daily_usage =
            total_cost !== "-" ? total_cost / interval_days : 0;

          const average_daily_usage_percent =
            (average_daily_usage * 100) /
            (row.original?.budget_daily_amount || 0);

          const average_daily_usage_percent_background_color = ["-"].includes(
            total_cost,
          )
            ? "transparent"
            : parseFloat(average_daily_usage_percent) >=
                max_average_use_daily_budget
              ? "#ea0505"
              : parseFloat(average_daily_usage_percent) <=
                  min_average_use_daily_budget
                ? "#07a707"
                : "rgb(244, 176, 79)";

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div>
                {total_budget && (
                  <p
                    className="text-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUpdateChannelCampaignGroup({
                        id: row.original.id,
                        channel_id,
                        name: row.original.name,
                        status: row.original.status,
                        channel_campaign_id: row.original.channel_campaign_id,
                        sales_period_standard_cpc:
                          row.original.sales_period_standard_cpc,
                        budget_daily_amount: row.original.budget_daily_amount,
                        sales_period_min_cpc_percent:
                          row.original.sales_period_min_cpc_percent,
                        sales_period_max_cpc_percent:
                          row.original.sales_period_max_cpc_percent,
                        sales_period_min_cpc: row.original.sales_period_min_cpc,
                        sales_period_max_cpc: row.original.sales_period_max_cpc,
                        bidding_max_cpc_amount:
                          row.original.bidding_max_cpc_amount,
                      });
                    }}
                  >
                    {parseFloat(used_budget).toFixed(2)}
                    {"/"}
                    {parseFloat(total_budget).toFixed(2)}{" "}
                    {row.original?.budget_daily_currency}
                  </p>
                )}
                <ProgressBar
                  available_budget={available_budget}
                  total_budget={total_budget}
                />
              </div>
              <span
                style={
                  total_cost !== "-"
                    ? {
                        display: "inline-block",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        minWidth: 14,
                        padding: "2px 4px",
                        borderRadius: 2,
                        marginRight: 2,
                        color: "#fff",
                        backgroundColor:
                          average_daily_usage_percent_background_color,
                      }
                    : {}
                }
              >
                {average_daily_usage.toFixed(2)} <br />
                {average_daily_usage_percent.toFixed(0)}%
              </span>
            </div>
          );
        },
      },
      {
        id: "cpc",
        header: () => (
          <>
            <span>CPC</span>
            <p className="extra">Efekt. stawka</p>
          </>
        ),
        cell: ({ row }) =>
          !row.original?.is_custom_row && (
            <>
              <span style={{ whiteSpace: "nowrap" }}>
                {row.original?.bidding_max_cpc_amount ? (
                  <span
                    className="text-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUpdateChannelCampaignGroup({
                        id: row.original.id,
                        channel_id,
                        name: row.original.name,
                        status: row.original.status,
                        channel_campaign_id: row.original.channel_campaign_id,
                        sales_period_standard_cpc:
                          row.original.sales_period_standard_cpc,
                        budget_daily_amount: row.original.budget_daily_amount,
                        sales_period_min_cpc_percent:
                          row.original.sales_period_min_cpc_percent,
                        sales_period_max_cpc_percent:
                          row.original.sales_period_max_cpc_percent,
                        sales_period_min_cpc: row.original.sales_period_min_cpc,
                        sales_period_max_cpc: row.original.sales_period_max_cpc,
                        bidding_max_cpc_amount:
                          row.original.bidding_max_cpc_amount,
                      });
                    }}
                  >
                    {row.original.sales_period_standard_cpc
                      ? parseFloat(
                          row.original.sales_period_standard_cpc,
                        ).toFixed(2)
                      : "-"}{" "}
                    {row.original?.bidding_max_cpc_currency}
                  </span>
                ) : (
                  "-"
                )}
              </span>
              <p style={{ whiteSpace: "nowrap" }}>
                <span>
                  {fetchItemStats(
                    row.depth,
                    row.original.id,
                    row.original?.channel_campaign_group?.id,
                    row.original?.product?.id,
                    "average_cpc",
                    "PLN",
                  )}
                </span>{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatsModalData({
                      name: row.original.name || row.original.product?.name,
                      default_columns: ["average_cpc"],
                      ...fetchStatsModalProps(
                        row.depth,
                        row.original.id,
                        row.original?.channel_campaign_id,
                        row.original?.channel_campaign_group?.id,
                        row.original?.product?.id,
                      ),
                    });
                  }}
                >
                  <span>📈</span>
                </button>
              </p>
            </>
          ),
      },
      {
        id: "total_views",
        header: () => <span>Odsłony</span>,
        cell: ({ row }) =>
          !row.original?.is_custom_row && (
            <span style={{ whiteSpace: "nowrap" }}>
              <span>
                {fetchItemStats(
                  row.depth,
                  row.original.id,
                  row.original?.channel_campaign_group?.id,
                  row.original?.product?.id,
                  "total_views",
                )}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatsModalData({
                    name: row.original.name || row.original.product?.name,
                    default_columns: ["total_views"],
                    ...fetchStatsModalProps(
                      row.depth,
                      row.original.id,
                      row.original?.channel_campaign_id,
                      row.original?.channel_campaign_group?.id,
                      row.original?.product?.id,
                    ),
                  });
                }}
              >
                <span>📈</span>
              </button>
            </span>
          ),
      },
      {
        id: "total_clicks",
        header: () => <span>Kliknięcia</span>,
        cell: ({ row }) =>
          !row.original?.is_custom_row && (
            <span style={{ whiteSpace: "nowrap" }}>
              <span>
                {fetchItemStats(
                  row.depth,
                  row.original.id,
                  row.original?.channel_campaign_group?.id,
                  row.original?.product?.id,
                  "total_clicks",
                )}
              </span>{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatsModalData({
                    name: row.original.name || row.original.product?.name,
                    default_columns: ["total_clicks"],
                    ...fetchStatsModalProps(
                      row.depth,
                      row.original.id,
                      row.original?.channel_campaign_id,
                      row.original?.channel_campaign_group?.id,
                      row.original?.product?.id,
                    ),
                  });
                }}
              >
                <span>📈</span>
              </button>
            </span>
          ),
      },
      {
        id: "cost",
        header: () => <span>Koszt</span>,
        cell: ({ row }) => {
          if (row.original?.is_custom_row) {
            return null;
          }
          const total_cost_raw = fetchItemStats(
            row.depth,
            row.original.id,
            row.original?.channel_campaign_group?.id,
            row.original?.product?.id,
            "total_cost",
          );
          const total_cost = fetchItemStats(
            row.depth,
            row.original.id,
            row.original?.channel_campaign_group?.id,
            row.original?.product?.id,
            "total_cost",
            "PLN",
          );

          if (parseFloat(total_cost_raw) === 0) {
            return <span style={{ whiteSpace: "nowrap" }}>-</span>;
          }

          return (
            <span style={{ whiteSpace: "nowrap" }}>
              <span>{total_cost}</span>{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatsModalData({
                    name: row.original.name || row.original.product?.name,
                    default_columns: ["total_cost"],
                    ...fetchStatsModalProps(
                      row.depth,
                      row.original.id,
                      row.original?.channel_campaign_id,
                      row.original?.channel_campaign_group?.id,
                      row.original?.product?.id,
                    ),
                  });
                }}
              >
                <span>📈</span>
              </button>
            </span>
          );
        },
      },
      {
        id: "sold_amount",
        header: () => <span>Wartość sprzedaży</span>,
        cell: ({ row }) => {
          if (row.original?.is_custom_row) {
            return null;
          }
          const total_cost_raw = fetchItemStats(
            row.depth,
            row.original.id,
            row.original?.channel_campaign_group?.id,
            row.original?.product?.id,
            "total_cost",
          );

          if (parseFloat(total_cost_raw) === 0) {
            return <span style={{ whiteSpace: "nowrap" }}>-</span>;
          }

          return (
            <span style={{ whiteSpace: "nowrap" }}>
              <span>
                {fetchItemStats(
                  row.depth,
                  row.original.id,
                  row.original?.channel_campaign_group?.id,
                  row.original?.product?.id,
                  "sold_amount",
                  "PLN",
                )}
              </span>{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatsModalData({
                    name: row.original.name || row.original.product?.name,
                    default_columns: ["sold_amount"],
                    ...fetchStatsModalProps(
                      row.depth,
                      row.original.id,
                      row.original?.channel_campaign_id,
                      row.original?.channel_campaign_group?.id,
                      row.original?.product?.id,
                    ),
                  });
                }}
              >
                <span>📈</span>
              </button>
            </span>
          );
        },
      },
      {
        id: "roas",
        header: () => <span>ROAS</span>,
        cell: ({ row }) => {
          if (row.original?.is_custom_row) {
            return null;
          }

          const total_cost_raw = fetchItemStats(
            row.depth,
            row.original.id,
            row.original?.channel_campaign_group?.id,
            row.original?.product?.id,
            "total_cost",
          );

          if (parseFloat(total_cost_raw) === 0) {
            return <span style={{ whiteSpace: "nowrap" }}>-</span>;
          }

          const roas = fetchItemStats(
            row.depth,
            row.original.id,
            row.original?.channel_campaign_group?.id,
            row.original?.product?.id,
            "roas",
          );

          const roas_background_color = ["-"].includes(roas)
            ? "transparent"
            : parseFloat(roas) >= max_roas
              ? "#07a707"
              : parseFloat(roas) <= min_roas
                ? "#ea0505"
                : "#b4b4b4";

          return (
            <span style={{ whiteSpace: "nowrap" }}>
              <span
                style={
                  roas !== "-"
                    ? {
                        display: "inline-block",
                        textAlign: "center",
                        minWidth: 14,
                        padding: "0 3px",
                        borderRadius: 2,
                        marginRight: 2,
                        color: "#fff",
                        backgroundColor: roas_background_color,
                      }
                    : {}
                }
              >
                {roas}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatsModalData({
                    name: row.original.name || row.original.product?.name,
                    default_columns: ["roas"],
                    ...fetchStatsModalProps(
                      row.depth,
                      row.original.id,
                      row.original?.channel_campaign_id,
                      row.original?.channel_campaign_group?.id,
                      row.original?.product?.id,
                    ),
                  });
                }}
              >
                <span>📈</span>
              </button>
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [
      data,
      campaigns_stats,
      campaigns_today_cost,
      loading_rows,
      loaded_children,
      moving_products,
    ],
  );

  React.useEffect(() => {
    Object.entries(expanded).forEach(([rowId, isExpanded]) => {
      if (isExpanded && !loaded_children[rowId]) {
        const row = table.getRowModel().rowsById[rowId];
        if (row) {
          const channel_campaign_id =
            row.original.type === "channel_campaign"
              ? row.original.id
              : row.original.channel_campaign_id;

          const channel_campaign_group_id = [
            "channel_campaign_group",
            "archival_offers_channel_campaign_group",
            "waiting_channel_campaign_group_products",
          ].includes(row.original.type)
            ? row.original.id
            : null;

          fetchChildren({
            row_id: row.id,
            type: row.original?.type,
            kind: row.original?.kind,
            channel_campaign_id,
            channel_campaign_group_id,
          });
        }
      }
    });
  }, [expanded, loaded_children, fetchChildren]);

  const table_data = React.useMemo(() => {
    return [...data];
  }, [data, loaded_children, moving_products, loading_rows]);

  const table = useReactTable({
    data: table_data,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => loaded_children?.[`${row.type}_${row.id}`] || [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) =>
      row?.original?.channel_campaign_groups?.length > 0 ||
      row?.original?.channel_campaign_group_product_ids?.length > 0 ||
      row?.original?.is_custom_row,
    getRowId: (row) => `${row.type}_${row.id}`,
  });

  const moveProduct = useCallback(
    async ({
      row_id,
      channel_campaign_id,
      channel_campaign_group_id,
      channel_campaign_group_product_id,
      new_channel_campaign_group_id,
    }) => {
      try {
        setMovingProducts((prev) => ({
          ...prev,
          [row_id]: true,
          [`channel_campaign_${channel_campaign_id}`]: true,
          [`channel_campaign_group_${new_channel_campaign_group_id}`]: true,
        }));

        await moveChannelCampaignGroupProduct({
          channel_id,
          channel_campaign_id,
          channel_campaign_group_id,
          channel_campaign_group_product_id,
          new_channel_campaign_group_id,
        });

        fetchChildren({
          row_id: `channel_campaign_${channel_campaign_id}`,
          type: "channel_campaign",
          channel_campaign_id,
          refetch: true,
        });

        fetchChildren({
          row_id: `channel_campaign_group_${channel_campaign_group_id}`,
          type: "channel_campaign_group",
          channel_campaign_group_id,
          channel_campaign_id,
          refetch: true,
        });

        fetchChildren({
          row_id: `channel_campaign_group_${new_channel_campaign_group_id}`,
          type: "channel_campaign_group",
          channel_campaign_group_id: new_channel_campaign_group_id,
          channel_campaign_id,
          refetch: true,
        });

        setCampaignToSync(channel_campaign_id);
      } catch (e) {
        console.error(e);
      } finally {
        setMovingProducts((prev) => ({ ...prev, [row_id]: false }));
      }
    },
    [channel_id, fetchChildren],
  );

  const removeProduct = useCallback(
    async ({
      row_id,
      channel_campaign_id,
      channel_campaign_group_id,
      channel_campaign_group_product_id,
    }) => {
      try {
        setMovingProducts((prev) => ({
          ...prev,
          [row_id]: true,
          [`channel_campaign_${channel_campaign_id}`]: true,
          [`channel_campaign_group_${channel_campaign_group_id}`]: true,
          [`waiting_channel_campaign_group_products_${channel_campaign_group_id}`]: true,
        }));
        await removeChannelCampaignGroupProduct({
          channel_id,
          channel_campaign_id,
          channel_campaign_group_id,
          channel_campaign_group_product_id,
        });

        await fetchChildren({
          row_id: `channel_campaign_${channel_campaign_id}`,
          type: "channel_campaign",
          channel_campaign_id,
          refetch: true,
        });
        await fetchChildren({
          row_id: `channel_campaign_group_${channel_campaign_group_id}`,
          type: "channel_campaign_group",
          channel_campaign_group_id,
          channel_campaign_id,
          refetch: true,
          kind: "neutral",
        });
        fetchChildren({
          row_id: `waiting_channel_campaign_group_products_${channel_campaign_group_id}`,
          type: "waiting_channel_campaign_group_products",
          channel_campaign_group_id,
          channel_campaign_id,
          refetch: true,
        });
        setCampaignToSync(channel_campaign_id);
      } catch (e) {
        console.error(e);
      } finally {
        setMovingProducts((prev) => ({ ...prev, [row_id]: false }));
      }
    },
    [channel_id, fetchChildren],
  );

  const addProduct = useCallback(
    async ({
      row_id,
      channel_campaign_id,
      channel_campaign_group_id,
      product_id,
    }) => {
      try {
        setMovingProducts((prev) => ({
          ...prev,
          [row_id]: true,
          [`channel_campaign_${channel_campaign_id}`]: true,
          [`channel_campaign_group_${channel_campaign_group_id}`]: true,
          [`waiting_channel_campaign_group_products_${channel_campaign_group_id}`]: true,
        }));
        await addChannelCampaignGroupProduct({
          channel_id,
          channel_campaign_id,
          channel_campaign_group_id,
          product_id,
        });
        await fetchChildren({
          row_id: `channel_campaign_${channel_campaign_id}`,
          type: "channel_campaign",
          channel_campaign_id,
          refetch: true,
        });
        await fetchChildren({
          row_id: `channel_campaign_group_${channel_campaign_group_id}`,
          type: "channel_campaign_group",
          channel_campaign_group_id,
          channel_campaign_id,
          refetch: true,
          kind: "neutral",
        });
        fetchChildren({
          row_id: `waiting_channel_campaign_group_products_${channel_campaign_group_id}`,
          type: "waiting_channel_campaign_group_products",
          channel_campaign_group_id,
          channel_campaign_id,
          refetch: true,
        });
        setCampaignToSync(channel_campaign_id);
      } catch (e) {
        console.error(e);
      } finally {
        setMovingProducts((prev) => ({
          ...prev,
          [row_id]: false,
          [`channel_campaign_${channel_campaign_id}`]: false,
          [`channel_campaign_group_${channel_campaign_group_id}`]: false,
          [`waiting_channel_campaign_group_products_${channel_campaign_group_id}`]: false,
        }));
      }
    },
    [channel_id, fetchChildren],
  );

  const main_filters = useMemo(
    () =>
      [
        {
          type: "input",
          label: "Nazwa kampanii",
          name: "q[name_matches]",
        },
        {
          type: "input",
          label: "Nazwa grupy kampanii",
          name: "q[channel_campaign_groups_name_matches]",
        },
        {
          type: "select",
          label: "Statystyki za",
          name: "interval",
          options: [
            { label: "Ostatnie 30 dni", value: "30_days" },
            { label: "Ostatnie 14 dni", value: "14_days" },
            { label: "Ostatnie 7 dni", value: "7_days" },
          ],
        },
        {
          type: "select",
          label: "Pokaż zarchiwizowane",
          name: "show_archived",
          options: [
            { label: "Tak", value: true },
            { label: "Nie", value: false },
          ],
        },
      ].filter(Boolean),
    [],
  );

  return (
    <div className="product-workflow-list">
      <MainFilters
        data={main_filters}
        current_filters={query_filters}
        setQueryFilters={handleSetQueryFilters}
      />
      {update_channel_campaign_group?.id && (
        <UpdateChannelCampaignGroupModal
          {...update_channel_campaign_group}
          handleClose={() => setUpdateChannelCampaignGroup(null)}
          initialValues={{
            sales_period_standard_cpc:
              update_channel_campaign_group.sales_period_standard_cpc,
            budget_daily_amount:
              update_channel_campaign_group.budget_daily_amount,
            status: update_channel_campaign_group.status,
            sales_period_min_cpc_percent:
              update_channel_campaign_group.sales_period_min_cpc_percent,
            sales_period_max_cpc_percent:
              update_channel_campaign_group.sales_period_max_cpc_percent,
          }}
          handleConfirm={(data) => {
            setLoadedChildren((prev) => {
              const currentCampaignKey = `channel_campaign_${update_channel_campaign_group.channel_campaign_id}`;
              if (!prev[currentCampaignKey]) return prev;
              return {
                ...prev,
                [currentCampaignKey]: prev[currentCampaignKey].map((item) => {
                  if (item.id === data.id) {
                    return {
                      ...item,
                      bidding_max_cpc_amount: data.bidding_max_cpc_amount,
                      sales_period_standard_cpc: data.sales_period_standard_cpc,
                      sales_period_min_cpc_percent:
                        data.sales_period_min_cpc_percent,
                      sales_period_max_cpc_percent:
                        data.sales_period_max_cpc_percent,
                      sales_period_min_cpc: data.sales_period_min_cpc,
                      sales_period_max_cpc: data.sales_period_max_cpc,
                      budget_daily_amount: data.budget_daily_amount,
                      status: data.status,
                      sync_needed: data.sync_needed,
                    };
                  }
                  return item;
                }),
              };
            });
            setCampaignToSync(
              update_channel_campaign_group.channel_campaign_id,
            );
            setUpdateChannelCampaignGroup(null);
          }}
        />
      )}
      {update_channel_campaign?.id && (
        <UpdateChannelCampaignModal
          {...update_channel_campaign}
          handleClose={() => setUpdateChannelCampaign(null)}
          initialValues={{
            status: update_channel_campaign.status,
          }}
          handleConfirm={() => {
            setExpanded({});
            setLoadedChildren({});
            setLoadingRows({});
            setMovingProducts({});
            setUpdateChannelCampaign(null);
            refetch();
          }}
        />
      )}
      {stats_modal_data?.name && (
        <StatsModal
          {...stats_modal_data}
          default_interval={query_filters?.interval}
          channel_id={channel_id}
          handleClose={() => setStatsModalData(null)}
        />
      )}
      <div className="product-workflow-list__table">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      ...getHeadStyle(header),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: getRowStyle(row),
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const column_id = cell.column.id.split("_").at(-1);
                  if (row.depth === 2 && ["id", "kind"].includes(column_id)) {
                    return (
                      <td key={cell.id} colSpan={column_id === "id" ? 3 : 2}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  }
                  if (
                    row.depth === 2 &&
                    ["status", "category", "placements"].includes(column_id)
                  ) {
                    return null;
                  }
                  return (
                    <td
                      key={`${cell.id}-${row.original?.id || row.index}`}
                      style={getCellStyle(cell)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="product-workflow-list__footer" ref={load_more_ref}>
        {meta.has_next_page && (
          <button
            className="btn btn-lg btn-blue"
            type="button"
            onClick={fetchNextPage}
            disabled={is_fetching_next_page}
          >
            {is_fetching_next_page ? (
              <span>Proszę czekać...</span>
            ) : (
              <>
                <svg
                  className="m-r-10"
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="none"
                    d="M9 1L5 5L1 1"
                    stroke="#fff"
                    strokeLinecap="round"
                    strokeWidth="2"
                  />
                </svg>
                <span>Pokaż więcej</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default CampaignsList;
